import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageContainer } from '@web/app/shared/components/PageContainer';
import { eachDayOfInterval, format, parseISO, startOfMonth } from 'date-fns';

import { useLogs } from '@codes/logs';
import { isLocalEnvironment } from '@codes/web-core';

import {
    DailyUsageChart,
    FilterSection,
    HourlyActivityChart,
    LogGeneratorModal,
    PerformanceMetricsTable,
    ServiceUsageChart,
    StatisticsCards,
} from '../components';

import type { TFunction } from 'i18next';

interface LogEntry {
    id: string;
    userId: string;
    userName: string;
    loginAt: string;
    logoutAt: string;
    duration: number;
    apiCallCount: number;
    avgResponseTime: number;
    errorCount: number;
    usedServices: string[];
}

const formatDuration = (seconds: number, t: TFunction): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        return t('logs.duration.hoursMinutes', { hours, minutes });
    }
    return t('logs.duration.minutes', { minutes });
};

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
const GRADIENT_COLORS = [
    { start: '#667eea', end: '#764ba2' },
    { start: '#f093fb', end: '#f5576c' },
    { start: '#4facfe', end: '#00f2fe' },
    { start: '#43e97b', end: '#38f9d7' },
];

export const LogsPage: React.FC = () => {
    const { t } = useTranslation();
    const [selectedCompany, setSelectedCompany] = useState('1000046');

    const getMinStartDate = (companyId: string): Date => {
        if (companyId === '1000049') {
            return new Date(2025, 7, 1); // 8월 1일 (month는 0부터 시작)
        }
        if (companyId === '1000061') {
            return new Date(2025, 10, 1); // 11월 1일 (month는 0부터 시작)
        }
        // 쓰리얼라이언스 2025-11-01~2025-12-31
        if (companyId === '1000066') {
            return new Date(2025, 10, 1); // 11월 1일 (month는 0부터 시작)
        }
        // 유니언하우스 2025-04-01~2025-11-30
        if (companyId === '1000067') {
            return new Date(2025, 3, 1); // 04월 1일 (month는 0부터 시작)
        }
        // 오늘카드 2025-11-01~2025-12-31
        if (companyId === '1000068') {
            return new Date(2025, 10, 1); // 11월 1일 (month는 0부터 시작)
        }
        // 2024년 회사들 (1000070~1000079): 2024-05-01 ~ 2024-12-31
        // 청년을담다, 지니박스, 슬코, 쉐어피스, 댕글, 정남옥 서울대입구점, 이노베이, 에스에스사업단, 올커넥터즈, 애니씨앤에스
        if (
            [
                '1000070',
                '1000071',
                '1000072',
                '1000073',
                '1000074',
                '1000075',
                '1000076',
                '1000077',
                '1000078',
                '1000079',
            ].includes(companyId)
        ) {
            return new Date(2024, 4, 1); // 5월 1일 (month는 0부터 시작)
        }
        // 2025년 회사들 (1000080~1000081): 2025-05-01 ~ 2025-12-31
        // 에코드인, 시큐어넷
        if (['1000080', '1000081'].includes(companyId)) {
            return new Date(2025, 4, 1); // 5월 1일 (month는 0부터 시작)
        }
        // 테크오일 2024-03-15 ~ 2024-04-14
        if (companyId === '1000082') {
            return new Date(2024, 2, 15); // 3월 15일
        }
        // 그로잉인터네셔날 2024-09-19 ~ 2026-03-31
        if (companyId === '1000083') {
            return new Date(2024, 8, 19); // 9월 19일
        }
        // 다트온닷 2025-09-15 ~ 2025-10-14
        if (companyId === '1000084') {
            return new Date(2025, 8, 15); // 9월 15일
        }
        // 앤느 2025-07-18 ~ 2025-12-17
        if (companyId === '1000085') {
            return new Date(2025, 6, 18); // 7월 18일
        }
        // 가브리엘 2025-09-09 ~ 2025-10-08
        if (companyId === '1000086') {
            return new Date(2025, 8, 9); // 9월 9일
        }
        // 이을 디벨롭먼트 2025-10-20 ~ 2026-04-19
        if (companyId === '1000087') {
            return new Date(2025, 9, 20); // 10월 20일
        }
        // 베스핀글로벌 2025-10-16 ~ 2026-03-31
        if (companyId === '1000088') {
            return new Date(2025, 9, 16); // 10월 16일
        }
        // 뉴로서킷 2025-11-21 ~ 2025-12-20
        if (companyId === '1000089') {
            return new Date(2025, 10, 21); // 11월 21일
        }
        return new Date(2025, 5, 1);
    };

    const getMaxEndDate = (companyId: string): Date => {
        const today = new Date();

        // 쓰리얼라이언스 2025-11-01~2025-12-31 (단, 오늘이 우선)
        if (companyId === '1000066') {
            const companyMax = new Date(2025, 11, 31); // 12월 31일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 유니언하우스 2025-04-01~2025-11-30 (단, 오늘이 우선)
        if (companyId === '1000067') {
            const companyMax = new Date(2025, 10, 30); // 11월 30일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 오늘카드 2025-11-01~2025-12-31 (단, 오늘이 우선)
        if (companyId === '1000068') {
            const companyMax = new Date(2025, 11, 31); // 12월 31일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 2024년 회사들 (1000070~1000079): 2024-05-01 ~ 2024-12-31 (단, 오늘이 우선)
        // 청년을담다, 지니박스, 슬코, 쉐어피스, 댕글, 정남옥 서울대입구점, 이노베이, 에스에스사업단, 올커넥터즈, 애니씨앤에스
        if (
            [
                '1000070',
                '1000071',
                '1000072',
                '1000073',
                '1000074',
                '1000075',
                '1000076',
                '1000077',
                '1000078',
                '1000079',
            ].includes(companyId)
        ) {
            const companyMax = new Date(2024, 11, 31); // 12월 31일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 2025년 회사들 (1000080~1000081): 2025-05-01 ~ 2025-12-31 (단, 오늘이 우선)
        // 에코드인, 시큐어넷
        if (['1000080', '1000081'].includes(companyId)) {
            const companyMax = new Date(2025, 11, 31); // 12월 31일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 테크오일 2024-03-15 ~ 2024-04-14
        if (companyId === '1000082') {
            const companyMax = new Date(2024, 3, 14); // 4월 14일
            return companyMax < today ? companyMax : today;
        }
        // 그로잉인터네셔날 2024-09-19 ~ 2026-03-31
        if (companyId === '1000083') {
            const companyMax = new Date(2026, 2, 31); // 3월 31일
            return companyMax < today ? companyMax : today;
        }
        // 다트온닷 2025-09-15 ~ 2025-10-14
        if (companyId === '1000084') {
            const companyMax = new Date(2025, 9, 14); // 10월 14일
            return companyMax < today ? companyMax : today;
        }
        // 앤느 2025-07-18 ~ 2025-12-17
        if (companyId === '1000085') {
            const companyMax = new Date(2025, 11, 17); // 12월 17일
            return companyMax < today ? companyMax : today;
        }
        // 가브리엘 2025-09-09 ~ 2025-10-08
        if (companyId === '1000086') {
            const companyMax = new Date(2025, 9, 8); // 10월 8일
            return companyMax < today ? companyMax : today;
        }
        // 이을 디벨롭먼트 2025-10-20 ~ 2026-04-19
        if (companyId === '1000087') {
            const companyMax = new Date(2026, 3, 19); // 4월 19일
            return companyMax < today ? companyMax : today;
        }
        // 베스핀글로벌 2025-10-16 ~ 2026-03-31
        if (companyId === '1000088') {
            const companyMax = new Date(2026, 2, 31); // 3월 31일
            return companyMax < today ? companyMax : today;
        }
        // 뉴로서킷 2025-11-21 ~ 2025-12-20
        if (companyId === '1000089') {
            const companyMax = new Date(2025, 11, 20); // 12월 20일
            return companyMax < today ? companyMax : today;
        }
        // 나머지는 당일까지
        return today;
    };

    const [startDate, setStartDate] = useState<Date>(startOfMonth(getMinStartDate('1000046')));
    const [endDate, setEndDate] = useState<Date>(getMaxEndDate('1000046'));
    const [refreshKey, setRefreshKey] = useState(0);
    const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);

    const { data, isFetching } = useLogs({
        userId: selectedCompany,
        from: format(startDate, 'yyyy-MM-dd'),
        to: format(endDate, 'yyyy-MM-dd'),
        key: refreshKey,
    });

    const statistics = useMemo(() => {
        const logList = data?.data || data?.list || [];
        if (!logList || logList.length === 0) {
            return {
                totalSessions: 0,
                totalApiCalls: 0,
                totalDuration: t('logs.duration.hours', { hours: 0 }),
                avgResponseTime: `0${t('logs.table.timeUnit.milliseconds')}`,
            };
        }

        const totalSessions = logList.length;
        // Only count API calls from logs that have associated services to match the Performance Metrics table
        const totalApiCalls = logList
            .filter((log: LogEntry) => log.usedServices.length > 0)
            .reduce((sum: number, log: LogEntry) => sum + log.apiCallCount, 0);
        const totalSeconds = logList.reduce((sum: number, log: LogEntry) => sum + log.duration, 0);

        const serviceUsage: Record<string, { count: number; avgResponse: number }> = {};
        logList.forEach((log: LogEntry) => {
            const servicesCount = log.usedServices.length;
            if (servicesCount === 0) {
                return;
            }

            log.usedServices.forEach(service => {
                if (!serviceUsage[service]) {
                    serviceUsage[service] = { count: 0, avgResponse: 0 };
                }
                serviceUsage[service].count += 1;
                serviceUsage[service].avgResponse += log.avgResponseTime;
            });
        });

        const apiAvgResponseTimes = Object.values(serviceUsage).map(data => Math.round(data.avgResponse / data.count));

        const avgResponseTime =
            apiAvgResponseTimes.length > 0
                ? Math.round(apiAvgResponseTimes.reduce((sum, avg) => sum + avg, 0) / apiAvgResponseTimes.length)
                : 0;

        return {
            totalSessions,
            totalApiCalls,
            totalDuration: formatDuration(totalSeconds, t),
            avgResponseTime: `${avgResponseTime}${t('logs.table.timeUnit.milliseconds')}`,
        };
    }, [data, t]);

    const apiCallData = useMemo(() => {
        const logList = data?.data || data?.list || [];
        if (!logList || logList.length === 0) {
            return [];
        }

        const serviceUsage: Record<string, { count: number; apiCalls: number; avgResponse: number }> = {};

        logList.forEach((log: LogEntry) => {
            const servicesCount = log.usedServices.length;
            if (servicesCount === 0) {
                return;
            }

            const baseApiCalls = Math.floor(log.apiCallCount / servicesCount);
            const remainder = log.apiCallCount % servicesCount;

            log.usedServices.forEach((service, index) => {
                if (!serviceUsage[service]) {
                    serviceUsage[service] = { count: 0, apiCalls: 0, avgResponse: 0 };
                }
                serviceUsage[service].count += 1;
                serviceUsage[service].apiCalls += baseApiCalls + (index < remainder ? 1 : 0);
                serviceUsage[service].avgResponse += log.avgResponseTime;
            });
        });

        const result = Object.entries(serviceUsage).map(([service, data]) => ({
            name: service.replace('lemon-', '').replace('-api', ''),
            fullName: service,
            [t('logs.charts.usageCount')]: data.count,
            [t('logs.charts.apiCallLabel')]: data.apiCalls,
            [t('logs.charts.avgResponseTimeLabel')]: Math.round(data.avgResponse / data.count),
        }));

        return result;
    }, [data, t]);

    const dailyUsageData = useMemo(() => {
        const logList = data?.data || data?.list || [];

        // Generate all dates in the selected range
        const allDates = eachDayOfInterval({
            start: startDate,
            end: endDate,
        });

        // Initialize all dates with zero values
        const dailyData: Record<
            string,
            { date: string; sessions: number; apiCalls: number; duration: number; errors: number }
        > = {};

        allDates.forEach(date => {
            const dateKey = format(date, 'MM-dd');
            dailyData[dateKey] = { date: dateKey, sessions: 0, apiCalls: 0, duration: 0, errors: 0 };
        });

        // Fill in actual log data
        if (logList && logList.length > 0) {
            logList.forEach((log: LogEntry) => {
                const date = format(parseISO(log.loginAt), 'MM-dd');
                if (dailyData[date]) {
                    dailyData[date].sessions += 1;
                    dailyData[date].apiCalls += log.apiCallCount;
                    dailyData[date].duration += Math.round(log.duration / 60);
                    dailyData[date].errors += log.errorCount;
                }
            });
        }

        return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
    }, [data, startDate, endDate]);

    const hourlyActivityData = useMemo(() => {
        const logList = data?.data || data?.list || [];
        if (!logList || logList.length === 0) {
            return [];
        }

        const hourlyData: Record<number, number> = {};
        for (let i = 0; i < 24; i++) {
            hourlyData[i] = 0;
        }

        logList.forEach((log: LogEntry) => {
            const hour = parseInt(format(parseISO(log.loginAt), 'HH'));
            hourlyData[hour] += 1;
        });

        return Object.entries(hourlyData).map(([hour, count]) => ({
            hour: t('logs.charts.hourLabel', { hour }),
            [t('logs.charts.activityCount')]: count,
        }));
    }, [data, t]);

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex bg-background w-full animate-enter">
                <PageContainer
                    maxWidth="full"
                    noPadding
                    className="flex-1 min-h-0 h-full py-8 px-8 max-md:px-4 space-y-6"
                >
                    <div className="h-12 bg-default rounded-lg w-full" />
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="h-24 bg-default rounded-lg animate-pulse" />
                        ))}
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="h-64 bg-default rounded-lg animate-pulse" />
                        ))}
                    </div>
                </PageContainer>
            </div>
        );
    }

    return (
        <div className="flex-1 flex bg-background w-full animate-enter">
            <PageContainer maxWidth="full" noPadding className="flex-1 min-h-0 h-full py-8 px-8 max-md:px-4 space-y-6">
                <FilterSection
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    getMinStartDate={getMinStartDate}
                    getMaxEndDate={getMaxEndDate}
                    handleRefresh={handleRefresh}
                    isFetching={isFetching}
                    onOpenGenerator={isLocalEnvironment() ? () => setIsGeneratorOpen(true) : undefined}
                />

                {isLocalEnvironment() && <LogGeneratorModal open={isGeneratorOpen} onOpenChange={setIsGeneratorOpen} />}

                <StatisticsCards statistics={statistics} />

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <ServiceUsageChart data={apiCallData} colors={COLORS} gradientColors={GRADIENT_COLORS} />

                    <DailyUsageChart data={dailyUsageData} />

                    <HourlyActivityChart data={hourlyActivityData} />

                    <PerformanceMetricsTable data={apiCallData} colors={COLORS} />
                </div>

                {/* 로그 콘솔 */}
                {/*<LogConsole*/}
                {/*    groupedLogs={groupedLogs}*/}
                {/*    expandedDates={expandedDates}*/}
                {/*    toggleDateExpansion={toggleDateExpansion}*/}
                {/*    formatDuration={formatDuration}*/}
                {/*/>*/}
            </PageContainer>
        </div>
    );
};
